// File: frontend/src/pages/fees/FeeCollection.jsx

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/axios';
import FeeReceipt from './FeeReceipt';
import PageHeader from '../../components/PageHeader';

const FeeCollection = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [feeStructures, setFeeStructures] = useState([]);

  // Payment Form State
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [discountAmount, setDiscountAmount] = useState(0);
  const [fineAmount, setFineAmount] = useState(0);
  const [paidAmount, setPaidAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [remarks, setRemarks] = useState('');

  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [generatedReceipt, setGeneratedReceipt] = useState(null);

  /**
   * Search student by name, father name, or student ID
   */
  const handleSearchStudent = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    try {
      setSearching(true);
      setError(null);
      setSelectedStudent(null);
      setFeeStructures([]);

      const response = await api.get('/api/v1/students', {
        params: { search: searchTerm.trim(), page: 1, limit: 10 }
      });

      if (response.data?.success) {
        const list = response.data.data || [];
        setSearchResults(list);
        if (list.length === 1) {
          selectStudent(list[0]);
        }
      } else {
        setError('No student records found.');
      }
    } catch (err) {
      console.error('Error searching student:', err);
      setError('Error searching student directory.');
    } finally {
      setSearching(false);
    }
  };

  /**
   * Select a student and load their class fee structure
   */
  const selectStudent = async (student) => {
    setSelectedStudent(student);
    setSearchResults([]);
    setError(null);

    try {
      // Fetch class fee structures
      const response = await api.get('/api/v1/fees/structures', {
        params: { class_id: student.class_id }
      });

      if (response.data?.success) {
        const structures = response.data.data || [];
        setFeeStructures(structures);

        // Pre-fill total structure amount as default paid amount
        const total = structures.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
        setPaidAmount(total > 0 ? String(total) : '500');
      }
    } catch (err) {
      console.error('Error fetching student fee structure:', err);
    }
  };

  // Calculations
  const grossTotal = feeStructures.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
  const discount = parseFloat(discountAmount) || 0;
  const fine = parseFloat(fineAmount) || 0;
  const netTotal = Math.max(grossTotal - discount + fine, 0);
  const paid = parseFloat(paidAmount) || 0;
  const balance = Math.max(netTotal - paid, 0);

  /**
   * Submit fee payment to backend API
   */
  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    if (!selectedStudent) {
      setError('Please select a student to collect fee.');
      return;
    }
    if (!paidAmount || paid <= 0) {
      setError('Please enter a valid paid amount.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const payload = {
        student_id: selectedStudent.id,
        class_id: selectedStudent.class_id,
        month: Number(month),
        year: Number(year),
        total_amount: grossTotal,
        discount_amount: discount,
        fine_amount: fine,
        paid_amount: paid,
        payment_method: paymentMethod,
        payment_date: paymentDate,
        remarks
      };

      const response = await api.post('/api/v1/fees/collect', payload);

      if (response.data?.success && response.data.data) {
        setGeneratedReceipt(response.data.data);
      } else {
        setError(response.data?.message || 'Failed to record fee payment.');
      }
    } catch (err) {
      console.error('Error recording fee collection:', err);
      setError(err.response?.data?.message || err.message || 'Error processing payment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Page Navigation Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <PageHeader
          title="Fee Collection"
          subtitle="Collect fee payment & issue official printed receipt"
          breadcrumbs={[
            { label: 'Fees', path: '/fees' },
            { label: 'Collect Fee' }
          ]}
          backUrl="/fees"
        >
          <Link
            to="/fees"
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-4 py-2 rounded-xl text-xs transition"
          >
            Fee Dashboard
          </Link>
        </PageHeader>
      </div>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        
        {/* Search Student Box */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm mb-6">
          <h2 className="font-bold text-slate-800 text-base mb-2">Step 1: Search & Select Student</h2>
          <form onSubmit={handleSearchStudent} className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder="Enter Student Name, Father Name, or Student ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:bg-white transition"
            />
            <button
              type="submit"
              disabled={searching}
              className="px-6 py-3 bg-primary hover:bg-blue-600 text-white font-semibold rounded-xl text-sm shadow-sm transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {searching ? 'Searching...' : 'Find Student'}
            </button>
          </form>

          {/* Search Results Dropdown */}
          {searchResults.length > 0 && (
            <div className="mt-4 border border-slate-200 rounded-xl divide-y divide-slate-100 bg-slate-50">
              {searchResults.map((st) => (
                <div
                  key={st.id}
                  onClick={() => selectStudent(st)}
                  className="p-3.5 hover:bg-white cursor-pointer transition flex items-center justify-between"
                >
                  <div>
                    <span className="font-bold text-slate-900 text-sm">{st.student_name}</span>
                    <span className="text-xs text-slate-500 ml-2">Father: {st.father_name}</span>
                  </div>
                  <div className="text-xs font-semibold text-primary">
                    ID: {st.student_id} • Class {st.class_id}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-800 rounded-xl text-sm flex items-center justify-between shadow-md">
            <div className="flex items-center gap-3">
              <span className="text-xl">⚠️</span>
              <div>
                <strong className="font-bold block text-red-900">Payment Error</strong>
                <span>{error}</span>
              </div>
            </div>
            <button onClick={() => setError(null)} className="font-bold text-red-600 hover:text-red-800 p-1">✕</button>
          </div>
        )}

        {/* Selected Student Details Card */}
        {selectedStudent && (
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center justify-center font-bold text-xl">
                {selectedStudent.student_name.slice(0, 2).toUpperCase()}
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-lg">{selectedStudent.student_name}</h3>
                <p className="text-xs text-slate-500">
                  ID: <span className="font-mono text-primary font-bold">{selectedStudent.student_id}</span> • Class: <span className="font-bold text-slate-700">Class {selectedStudent.class_id}</span> • Father: {selectedStudent.father_name}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">Phone: {selectedStudent.phone}</p>
              </div>
            </div>

            <button
              onClick={() => setSelectedStudent(null)}
              className="text-xs font-semibold text-slate-500 hover:text-slate-700 bg-slate-100 px-3 py-1.5 rounded-lg transition"
            >
              Change Student
            </button>
          </div>
        )}

        {/* Fee Payment Collection Form */}
        {selectedStudent && (
          <form onSubmit={handleSubmitPayment} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Left Column: Fee Structure Breakdown */}
            <div className="lg:col-span-2 space-y-6">
              
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4">
                <h3 className="font-bold text-slate-800 text-base border-b pb-3">Class Fee Breakdown</h3>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs font-sans">
                    <thead>
                      <tr className="border-b border-slate-200 text-slate-500 uppercase font-semibold">
                        <th className="py-2.5 px-3">Category</th>
                        <th className="py-2.5 px-3">Frequency</th>
                        <th className="py-2.5 px-3 text-right">Amount (Rs.)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
                      {feeStructures.length === 0 ? (
                        <tr>
                          <td colSpan="3" className="py-4 text-center text-slate-400">
                            Standard Tuition Fee Rate (Rs. 5,000)
                          </td>
                        </tr>
                      ) : (
                        feeStructures.map((fs) => (
                          <tr key={fs.id}>
                            <td className="py-2.5 px-3 font-semibold text-slate-900">{fs.category_name || 'Tuition Fee'}</td>
                            <td className="py-2.5 px-3 capitalize">{fs.frequency}</td>
                            <td className="py-2.5 px-3 text-right font-mono font-bold">Rs. {parseFloat(fs.amount).toLocaleString(undefined, { minimumFractionDigits: 0 })}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="pt-4 border-t border-slate-100 space-y-2 text-xs">
                  <div className="flex justify-between text-slate-600">
                    <span>Gross Fee Total:</span>
                    <span className="font-mono font-bold text-slate-800">Rs. {grossTotal.toLocaleString(undefined, { minimumFractionDigits: 0 })}</span>
                  </div>
                  <div className="flex justify-between text-emerald-700">
                    <span>Discount / Concession (-):</span>
                    <span className="font-mono font-bold">-Rs. {discount.toLocaleString(undefined, { minimumFractionDigits: 0 })}</span>
                  </div>
                  <div className="flex justify-between text-amber-700">
                    <span>Late Fine (+):</span>
                    <span className="font-mono font-bold">+Rs. {fine.toLocaleString(undefined, { minimumFractionDigits: 0 })}</span>
                  </div>
                  <div className="flex justify-between text-base font-extrabold text-slate-900 border-t pt-2">
                    <span>Net Amount Due:</span>
                    <span className="font-mono text-primary">Rs. {netTotal.toLocaleString(undefined, { minimumFractionDigits: 0 })}</span>
                  </div>
                </div>
              </div>

            </div>

            {/* Right Column: Payment Input Controls */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4 self-start">
              <h3 className="font-bold text-slate-800 text-base border-b pb-3">Payment Details</h3>

              {/* Month & Year */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-700 uppercase mb-1">Month</label>
                  <select
                    value={month}
                    onChange={(e) => setMonth(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                  >
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                      <option key={m} value={m}>Month {m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-700 uppercase mb-1">Year</label>
                  <select
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                  >
                    <option value={2025}>2025</option>
                    <option value={2026}>2026</option>
                    <option value={2027}>2027</option>
                  </select>
                </div>
              </div>

              {/* Discount & Fine */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-700 uppercase mb-1">Discount (Rs.)</label>
                  <input
                    type="number"
                    min="0"
                    value={discountAmount}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val !== '' && parseFloat(val) < 0) return;
                      setDiscountAmount(val);
                    }}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-700 uppercase mb-1">Fine (Rs.)</label>
                  <input
                    type="number"
                    min="0"
                    value={fineAmount}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val !== '' && parseFloat(val) < 0) return;
                      setFineAmount(val);
                    }}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                  />
                </div>
              </div>

              {/* Paid Amount */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider">
                    Paid Amount (Rs.) <span className="text-red-500">*</span>
                  </label>
                  {paidAmount !== '' && parseFloat(paidAmount) < 0 && (
                    <span className="text-[11px] font-semibold text-red-600">Cannot be negative</span>
                  )}
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={paidAmount}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val !== '' && parseFloat(val) < 0) return; // Prevent negative inputs
                    setPaidAmount(val);
                  }}
                  required
                  className={`w-full px-4 py-2.5 bg-slate-50 border rounded-xl text-base font-mono font-bold text-emerald-700 focus:outline-none focus:ring-2 ${
                    paidAmount !== '' && parseFloat(paidAmount) < 0 ? 'border-red-500 ring-2 ring-red-200' : 'border-slate-300 focus:ring-primary'
                  }`}
                />
              </div>

              {/* Remaining Balance Indicator */}
              <div className="p-3 bg-slate-50 rounded-xl text-xs flex justify-between font-semibold">
                <span className="text-slate-500">Remaining Balance:</span>
                <span className={`font-mono font-bold ${balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                  Rs. {balance.toLocaleString(undefined, { minimumFractionDigits: 0 })}
                </span>
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 uppercase mb-1">Payment Method</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs capitalize"
                >
                  <option value="cash">Cash</option>
                  <option value="card">Credit / Debit Card</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="online">Online Payment</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>

              {/* Payment Date */}
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 uppercase mb-1">Payment Date</label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                />
              </div>

              {/* Remarks */}
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 uppercase mb-1">Remarks</label>
                <input
                  type="text"
                  placeholder="Optional payment notes..."
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting || !paidAmount || parseFloat(paidAmount) <= 0}
                className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-sm shadow-md transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting ? 'Processing Payment...' : 'Submit Payment'}
              </button>

            </div>

          </form>
        )}

      </main>

      {/* Generated Receipt Popup Modal */}
      {generatedReceipt && (
        <FeeReceipt
          receiptData={generatedReceipt}
          onClose={() => setGeneratedReceipt(null)}
        />
      )}

    </div>
  );
};

export default FeeCollection;
